/**
 * methods.tsx — the typed Business Operating Model instruments.
 *
 * The business-operating-model pack has seven methods. `generative-canvas` renders the
 * typed `business-canvas` (see canvas.tsx); the other six rendered as GENERIC widgets
 * (a plain textarea, a three-field form) under-captured the operating model. This file
 * gives each its own purpose-built, TYPED component so every business method emits
 * structured evidence the rest of the pipeline can reason over — the same principle as
 * the business-canvas, applied to the whole pack:
 *
 *   artifact-walkthrough       → ArtifactWidget   (annotate an artifact's parts + signal)
 *   contextual-inquiry-prompt  → WorkflowWidget   (an actor→action→handoff→risk flow)
 *   critical-incident-capture  → IncidentWidget   (situation/decision/outcome/lesson)
 *   self-explanation-prompt    → RationaleWidget  (choice + reasons + assumptions + disconfirmer)
 *   scenario-vignette          → ScenarioWidget   (decision + who it affects + trade-off + caveat)
 *   card-sort                  → DecisionRightsWidget (assign each case to who owns the call)
 *
 * Each validates its `rawContent` against the arktype types in domain/business.ts.
 */
import { useState } from "react";
import type {
  ArtifactPart,
  WorkflowStep,
} from "../../domain/business.ts";
import { entryId } from "../../domain/business.ts";
import { EvidenceOut, Boundary } from "../lib/format.tsx";
import { AutoTextarea } from "../lib/auto-textarea.tsx";
import { useT } from "../lib/i18n.tsx";
import { Specimen, boundaryFor, firstSentence } from "../primitives/specimen.tsx";
import type { WidgetProps } from "../primitives/specimen.tsx";

// ── Small shared form atoms ──────────────────────────────────────────────────────
function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="field bm-field">
      <div className="field-label"><span>{label}</span>{hint ? <span className="bm-hint">{hint}</span> : null}</div>
      {children}
    </div>
  );
}

/** An editable list of short text lines (reasons, assumptions…) with add/remove. */
function LineList({ label, lines, placeholder, onChange }: { label: string; lines: readonly string[]; placeholder: string; onChange: (next: string[]) => void }): JSX.Element {
  return (
    <Field label={label}>
      <div className="bm-lines">
        {lines.map((ln, i) => (
          <div className="bm-line" key={i}>
            <span className="bm-line-mk" aria-hidden="true">▸</span>
            <AutoTextarea rows={1} className="field-input bm-line-inp" value={ln} placeholder={placeholder} onChange={(e) => onChange(lines.map((x, j) => (j === i ? e.target.value : x)))} />
            <button type="button" className="bm-x" aria-label="remove" onClick={() => onChange(lines.filter((_, j) => j !== i))}>✕</button>
          </div>
        ))}
        <button type="button" className="bm-add" onClick={() => onChange([...lines, ""])}>＋ {label.toLowerCase()}</button>
      </div>
    </Field>
  );
}

function RecordBtn({ disabled, onClick, label }: { disabled: boolean; onClick: () => void; label: string }): JSX.Element {
  return (
    <div className="btn-row">
      <button className="btn primary" disabled={disabled} onClick={onClick}>{label}</button>
    </div>
  );
}

// ── artifact-walkthrough → annotate an artifact's parts and the signal each sends ──
const ARTIFACT_SIGNALS = [
  { v: "works", glyph: "✓", label: "lands" },
  { v: "confuses", glyph: "⚠", label: "confuses" },
  { v: "missing", glyph: "○", label: "missing" },
] as const;

function ArtifactWidget({ primitive, emit }: WidgetProps): JSX.Element {
  const [artifact, setArtifact] = useState("");
  const [parts, setParts] = useState<ArtifactPart[]>(() => [{ id: entryId("part", "p", 1), part: "", purpose: "" }]);
  const [done, setDone] = useState(false);
  const filled = parts.filter((p) => p.part.trim() || p.purpose.trim());
  const ready = artifact.trim() !== "" && filled.length > 0;
  const setPart = (i: number, patch: Partial<ArtifactPart>): void => setParts((ps) => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  // Toggle the signal off by OMITTING the key (exactOptionalPropertyTypes forbids `undefined`).
  const toggleSignal = (i: number, v: NonNullable<ArtifactPart["signal"]>): void =>
    setParts((ps) => ps.map((p, j) => {
      if (j !== i) return p;
      if (p.signal === v) { const { signal: _drop, ...rest } = p; return rest; }
      return { ...p, signal: v };
    }));
  const confuses = filled.filter((p) => p.signal === "confuses").length;
  return (
    <Specimen
      p={primitive}
      evidence={done ? (
        <EvidenceOut rows={[
          ["elicitationPrimitive", primitive.slug],
          ["artifact", `"${artifact.trim()}"`],
          ["annotatedParts", `${filled.length}${confuses ? ` (${confuses} confuse)` : ""}`],
          ["evidenceBoundary", <Boundary key="b">annotated artifact trace · one artifact · review required</Boundary>],
        ]} />
      ) : undefined}
    >
      <Field label="The artifact" hint="a real one — a deck slide, pricing page, onboarding doc">
        <input className="field-input" value={artifact} placeholder="e.g. the 'governance' sales-deck slide" onChange={(e) => setArtifact(e.target.value)} />
      </Field>
      <Field label="Walk through its parts" hint="what each part is for — and whether it lands">
        <div className="bm-parts">
          {parts.map((p, i) => (
            <div className="bm-part" key={p.id}>
              <div className="bm-part-grid">
                <AutoTextarea rows={1} className="field-input bm-part-name" value={p.part} placeholder="part / section" onChange={(e) => setPart(i, { part: e.target.value })} />
                <AutoTextarea rows={1} className="field-input" value={p.purpose} placeholder="what it's for…" onChange={(e) => setPart(i, { purpose: e.target.value })} />
              </div>
              <div className="bm-signal">
                {ARTIFACT_SIGNALS.map((s) => (
                  <button key={s.v} type="button" className={`bm-sig sig-${s.v} ${p.signal === s.v ? "on" : ""}`} title={s.label} onClick={() => toggleSignal(i, s.v)}>
                    <span aria-hidden="true">{s.glyph}</span> {s.label}
                  </button>
                ))}
                <button type="button" className="bm-x" aria-label="remove part" onClick={() => setParts((ps) => ps.filter((_, j) => j !== i))}>✕</button>
              </div>
            </div>
          ))}
          <button type="button" className="bm-add" onClick={() => setParts((ps) => [...ps, { id: entryId("part", "p", ps.length + 1), part: "", purpose: "" }])}>＋ part</button>
        </div>
      </Field>
      <RecordBtn disabled={!ready} label="Record walkthrough" onClick={() => {
        setDone(true);
        emit({
          summary: `Walked “${artifact.trim()}” — ${filled.length} part(s)${confuses ? `, ${confuses} confuse` : ""}`,
          boundary: "annotated artifact trace · one artifact · review required",
          rawContent: { artifact: artifact.trim(), parts: filled },
        });
      }} />
    </Specimen>
  );
}

// ── contextual-inquiry → an actor → action → handoff → risk workflow map ──────────
function WorkflowWidget({ primitive, emit }: WidgetProps): JSX.Element {
  const [trigger, setTrigger] = useState("");
  const [steps, setSteps] = useState<WorkflowStep[]>(() => [{ id: entryId("step", "s", 1), action: "" }]);
  const [done, setDone] = useState(false);
  const filled = steps.filter((s) => s.action.trim());
  const ready = filled.length > 0;
  const setStep = (i: number, patch: Partial<WorkflowStep>): void => setSteps((ss) => ss.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const risks = filled.filter((s) => s.risk?.trim()).length;
  return (
    <Specimen
      p={primitive}
      evidence={done ? (
        <EvidenceOut rows={[
          ["elicitationPrimitive", primitive.slug],
          ["workflow", `${filled.length} step(s)`],
          ["risksFlagged", `${risks}`],
          ["evidenceBoundary", <Boundary key="b">workflow observation · self-report may drift from practice · review required</Boundary>],
        ]} />
      ) : undefined}
    >
      <Field label="Trigger" hint="what kicks this workflow off (optional)">
        <input className="field-input" value={trigger} placeholder="e.g. a signed contract lands" onChange={(e) => setTrigger(e.target.value)} />
      </Field>
      <div className="bm-flow">
        {steps.map((s, i) => (
          <div className="bm-step" key={s.id}>
            <span className="bm-step-n">{String(i + 1).padStart(2, "0")}</span>
            <div className="bm-step-body">
              <div className="bm-step-row">
                <AutoTextarea rows={1} className="field-input bm-step-actor" value={s.actor ?? ""} placeholder="who" onChange={(e) => setStep(i, { actor: e.target.value })} />
                <AutoTextarea rows={1} className="field-input" value={s.action} placeholder="does what…" onChange={(e) => setStep(i, { action: e.target.value })} />
              </div>
              <div className="bm-step-row">
                <AutoTextarea rows={1} className="field-input bm-step-meta" value={s.tool ?? ""} placeholder="with which tool" onChange={(e) => setStep(i, { tool: e.target.value })} />
                <AutoTextarea rows={1} className="field-input bm-step-meta" value={s.handoff ?? ""} placeholder="hands off to →" onChange={(e) => setStep(i, { handoff: e.target.value })} />
              </div>
              <AutoTextarea rows={1} className="field-input bm-step-risk" value={s.risk ?? ""} placeholder="⚠ what could go wrong here?" onChange={(e) => setStep(i, { risk: e.target.value })} />
            </div>
            <button type="button" className="bm-x" aria-label="remove step" onClick={() => setSteps((ss) => ss.filter((_, j) => j !== i))}>✕</button>
          </div>
        ))}
        <button type="button" className="bm-add" onClick={() => setSteps((ss) => [...ss, { id: entryId("step", "s", ss.length + 1), action: "" }])}>＋ step</button>
      </div>
      <RecordBtn disabled={!ready} label="Record workflow" onClick={() => {
        setDone(true);
        emit({
          summary: `Workflow${trigger.trim() ? ` (on ${trigger.trim()})` : ""}: ${filled.map((s) => s.action.trim()).join(" → ")}`,
          boundary: "workflow observation · self-report may drift from practice · review required",
          rawContent: { ...(trigger.trim() ? { trigger: trigger.trim() } : {}), steps: filled },
        });
      }} />
    </Specimen>
  );
}

// ── critical-incident → a structured operational incident ─────────────────────────
function IncidentWidget({ primitive, emit }: WidgetProps): JSX.Element {
  const [f, setF] = useState({ situation: "", action: "", outcome: "", rationale: "", segment: "", lesson: "" });
  const [done, setDone] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }): void => setF((s) => ({ ...s, [k]: e.target.value }));
  const ready = f.situation.trim() !== "" && f.action.trim() !== "" && f.outcome.trim() !== "";
  return (
    <Specimen
      p={primitive}
      evidence={done ? (
        <EvidenceOut rows={[
          ["elicitationPrimitive", primitive.slug],
          ["decision", `"${firstSentence(f.action)}"`],
          ["outcome", `"${firstSentence(f.outcome)}"`],
          ...(f.segment.trim() ? [["segment", f.segment.trim()] as [string, string]] : []),
          ["evidenceBoundary", <Boundary key="b">single incident · not yet doctrine · review required</Boundary>],
        ]} />
      ) : undefined}
    >
      <Field label="Situation" hint="a specific time — not the general case"><AutoTextarea rows={2} className="field-input" value={f.situation} placeholder="what was going on?" onChange={set("situation")} /></Field>
      <Field label="What you decided / did"><AutoTextarea rows={2} className="field-input" value={f.action} placeholder="the call you made" onChange={set("action")} /></Field>
      <Field label="Outcome"><AutoTextarea rows={2} className="field-input" value={f.outcome} placeholder="what happened as a result" onChange={set("outcome")} /></Field>
      <div className="bm-2col">
        <Field label="Why (rationale)"><AutoTextarea rows={1} className="field-input" value={f.rationale} placeholder="(optional)" onChange={set("rationale")} /></Field>
        <Field label="Segment affected"><input className="field-input" value={f.segment} placeholder="(optional)" onChange={set("segment")} /></Field>
      </div>
      <Field label="Lesson for the agent" hint="what should it do next time?"><AutoTextarea rows={1} className="field-input" value={f.lesson} placeholder="(optional)" onChange={set("lesson")} /></Field>
      <RecordBtn disabled={!ready} label="Record incident" onClick={() => {
        setDone(true);
        emit({
          summary: `${firstSentence(f.action)} → ${firstSentence(f.outcome)}`,
          boundary: "single incident · not yet doctrine · review required",
          rawContent: {
            situation: f.situation.trim(), action: f.action.trim(), outcome: f.outcome.trim(),
            ...(f.rationale.trim() ? { rationale: f.rationale.trim() } : {}),
            ...(f.segment.trim() ? { segment: f.segment.trim() } : {}),
            ...(f.lesson.trim() ? { lesson: f.lesson.trim() } : {}),
          },
        });
      }} />
    </Specimen>
  );
}

// ── self-explanation → a rationale (choice + reasons + assumptions + disconfirmer) ─
function RationaleWidget({ primitive, emit, seed }: WidgetProps): JSX.Element {
  const [choice, setChoice] = useState(seed?.trim() ?? "");
  const [reasons, setReasons] = useState<string[]>([""]);
  const [assumptions, setAssumptions] = useState<string[]>([]);
  const [disconfirmer, setDisconfirmer] = useState("");
  const [done, setDone] = useState(false);
  const cleanReasons = reasons.map((r) => r.trim()).filter(Boolean);
  const ready = choice.trim() !== "" && cleanReasons.length > 0;
  return (
    <Specimen
      p={primitive}
      evidence={done ? (
        <EvidenceOut rows={[
          ["elicitationPrimitive", primitive.slug],
          ["choice", `"${firstSentence(choice)}"`],
          ["reasons", `${cleanReasons.length}`],
          ...(disconfirmer.trim() ? [["wouldChangeIf", `"${firstSentence(disconfirmer)}"`] as [string, string]] : []),
          ["evidenceBoundary", <Boundary key="b">explanation record · post-hoc rationalization risk · review required</Boundary>],
        ]} />
      ) : undefined}
    >
      <Field label="The choice you made"><AutoTextarea rows={1} className="field-input" value={choice} placeholder="what you decided" onChange={(e) => setChoice(e.target.value)} /></Field>
      <LineList label="Reasons" lines={reasons} placeholder="because…" onChange={setReasons} />
      <LineList label="Assumptions it rests on" lines={assumptions} placeholder="this holds only if…" onChange={setAssumptions} />
      <Field label="What would change your answer?" hint="a disconfirmer guards against post-hoc justification"><AutoTextarea rows={1} className="field-input" value={disconfirmer} placeholder="I'd decide differently if…" onChange={(e) => setDisconfirmer(e.target.value)} /></Field>
      <RecordBtn disabled={!ready} label="Record rationale" onClick={() => {
        setDone(true);
        const cleanAssumptions = assumptions.map((a) => a.trim()).filter(Boolean);
        emit({
          summary: `Chose “${firstSentence(choice)}” for ${cleanReasons.length} reason(s)`,
          boundary: "explanation record · post-hoc rationalization risk · review required",
          rawContent: {
            choice: choice.trim(), reasons: cleanReasons,
            ...(cleanAssumptions.length ? { assumptions: cleanAssumptions } : {}),
            ...(disconfirmer.trim() ? { disconfirmer: disconfirmer.trim() } : {}),
          },
        });
      }} />
    </Specimen>
  );
}

// ── scenario-vignette → a business decision with who it affects + the trade-off ────
const SCEN_FALLBACK = [
  { m: "A", t: "Act now — the most likely correct move." },
  { m: "B", t: "Gather more context first, then act." },
  { m: "C", t: "Escalate to a human — the tension needs a person." },
];
function ScenarioWidget({ primitive, emit, stimulus }: WidgetProps): JSX.Element {
  const opts = stimulus?.options && stimulus.options.length >= 2 ? stimulus.options.map((o) => ({ m: o.marker, t: o.text })) : SCEN_FALLBACK;
  const vignette = stimulus?.scenario?.trim() || primitive.prompt;
  const [sel, setSel] = useState<number | null>(null);
  const [affects, setAffects] = useState("");
  const [tradeoff, setTradeoff] = useState("");
  const [caveat, setCaveat] = useState("");
  return (
    <Specimen
      p={primitive}
      evidence={sel !== null ? (
        <EvidenceOut rows={[
          ["elicitationPrimitive", primitive.slug],
          ["decision", `"${opts[sel]!.t}"`],
          ...(affects.trim() ? [["affects", affects.trim()] as [string, string]] : []),
          ...(tradeoff.trim() ? [["tradeoff", `"${firstSentence(tradeoff)}"`] as [string, string]] : []),
          ["evidenceBoundary", <Boundary key="b">hypothetical vignette · validate against real cases before doctrine</Boundary>],
        ]} />
      ) : undefined}
    >
      <div className="vig">{vignette}</div>
      {opts.map((o, i) => (
        <button key={o.m} className={`opt ${sel === i ? "sel" : ""}`} onClick={() => setSel(i)}><span className="mk">{o.m}</span>{o.t}</button>
      ))}
      <div className="bm-2col" style={{ marginTop: 10 }}>
        <Field label="Who does this affect?"><input className="field-input" value={affects} placeholder="segment / stakeholder" onChange={(e) => setAffects(e.target.value)} /></Field>
        <Field label="Trade-off accepted"><input className="field-input" value={tradeoff} placeholder="what you give up" onChange={(e) => setTradeoff(e.target.value)} /></Field>
      </div>
      <Field label="Caveat that should travel with it"><input className="field-input" value={caveat} placeholder="(optional)" onChange={(e) => setCaveat(e.target.value)} /></Field>
      <RecordBtn disabled={sel === null} label="Record decision" onClick={() => emit({
        summary: `Chose ${opts[sel!]!.t}${tradeoff.trim() ? ` (trade-off: ${firstSentence(tradeoff)})` : ""}`,
        boundary: "hypothetical vignette · validate against real cases before doctrine",
        rawContent: {
          option: opts[sel!]!.m, decision: opts[sel!]!.t,
          ...(affects.trim() ? { affects: affects.trim() } : {}),
          ...(tradeoff.trim() ? { tradeoff: tradeoff.trim() } : {}),
          ...(caveat.trim() ? { caveat: caveat.trim() } : {}),
        },
      })} />
    </Specimen>
  );
}

// ── card-sort (business) → decision rights: who owns each call ─────────────────────
const DR_CASES = ["A routine, low-stakes case", "A high-value but ambiguous case", "A case with possible harm", "A novel case with no precedent", "A case peers disagree on"];
const DR_LANES = [
  { id: "agent", label: "Agent decides" },
  { id: "human", label: "Human reviews" },
  { id: "escalate", label: "Escalate" },
];
function DecisionRightsWidget({ primitive, emit, stimulus }: WidgetProps): JSX.Element {
  const cases = stimulus?.cards && stimulus.cards.length >= 2 ? [...stimulus.cards] : (stimulus?.items && stimulus.items.length >= 2 ? [...stimulus.items] : DR_CASES);
  const lanes = stimulus?.buckets && stimulus.buckets.length >= 2 ? [...stimulus.buckets] : DR_LANES;
  const [assign, setAssign] = useState<Record<number, string>>({});
  const placed = Object.keys(assign).length;
  const byLane = (): Record<string, string[]> => Object.fromEntries(lanes.map((l) => [l.id, Object.entries(assign).filter(([, v]) => v === l.id).map(([i]) => cases[Number(i)]!)]));
  return (
    <Specimen
      p={primitive}
      evidence={placed > 0 ? (
        <EvidenceOut rows={[
          ["elicitationPrimitive", primitive.slug],
          ...lanes.map((l) => [l.label, `[${Object.entries(assign).filter(([, v]) => v === l.id).map(([i]) => cases[Number(i)]!).join(" · ")}]`] as [string, string]),
          ["assigned", `${placed}/${cases.length}`],
          ["evidenceBoundary", <Boundary key="b">decision rights · case set ≠ exhaustive world · review required</Boundary>],
        ]} />
      ) : undefined}
    >
      <div className="bm-rights">
        {cases.map((c, i) => (
          <div className="bm-right-row" key={c}>
            <span className="bm-right-case">{c}</span>
            <div className="bm-right-lanes">
              {lanes.map((l) => (
                <button key={l.id} type="button" className={`bm-lane lane-${l.id} ${assign[i] === l.id ? "on" : ""}`} onClick={() => setAssign((a) => {
                  if (a[i] === l.id) { const n = { ...a }; delete n[i]; return n; }
                  return { ...a, [i]: l.id };
                })}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <RecordBtn disabled={placed === 0} label="Record decision rights" onClick={() => emit({
        summary: `Decision rights — ${lanes.map((l) => `${l.label}: ${Object.entries(assign).filter(([, v]) => v === l.id).length}`).join(", ")}`,
        boundary: "decision rights · case set ≠ exhaustive world · review required",
        rawContent: { lanes, assignments: byLane() },
      })} />
    </Specimen>
  );
}

/** The typed component for each non-canvas method of the business-operating-model pack.
 *  Keyed by primitive slug; resolved pack-scoped in Primitive.tsx. (generative-canvas is
 *  intentionally absent — its generic `canvas` widget already renders the typed business-canvas.) */
export const BUSINESS_METHOD_WIDGETS: Record<string, (p: WidgetProps) => JSX.Element> = {
  "artifact-walkthrough": ArtifactWidget,
  "contextual-inquiry-prompt": WorkflowWidget,
  "critical-incident-capture": IncidentWidget,
  "self-explanation-prompt": RationaleWidget,
  "scenario-vignette": ScenarioWidget,
  "card-sort": DecisionRightsWidget,
};
